import { ExportClassificationResult } from 'api/ExportHubAPI';

/**
 * Derives the "do we need an export licence for this product?" answer from the
 * Country Screening check — the Commerce Country Chart cross-reference of the
 * product's ECCN against each intended destination (spec §4.3) — unless a
 * reviewer has answered it by hand, which wins.
 *
 * The answer is per-destination, so a product needing a licence for one country
 * and not another reads as Yes with those countries named. "Unknown" is a real
 * third state, not a No: an unrun check or a missing ECCN can't clear anything.
 */

export type LicenseRequiredValue = 'Yes' | 'No' | 'Unknown';

/** A hand-entered answer. Screening can only produce the three values above. */
export type LicenseOverride = 'Yes' | 'No';

/** Whether the displayed answer was typed by a person or derived from a check. */
export type LicenseRequiredSource = 'manual' | 'screening';

export interface LicenseRequirement {
  value: LicenseRequiredValue;
  /** Destinations that require a licence (only populated when value is Yes). */
  countries: string[];
  /** One-line explanation shown under the answer. */
  detail: string;
  source: LicenseRequiredSource;
}

/**
 * The manual answer lives in its own `license_required_override` column, not in
 * `jurisdiction_details`. That blob doubles as the "is the jurisdiction
 * paperwork complete?" flag — NEEDS_JURISDICTION_PREDICATE tests it against
 * '{}' in SQL and hasJurisdictionDetail counts its keys — so a non-jurisdiction
 * key in it would drop a controlled product out of the Needs Jurisdiction
 * filter while its USML category was still missing.
 *
 * Column is tri-state: null = no manual answer, true = Yes, false = No.
 */
export const readLicenseOverride = (
  value: boolean | null | undefined,
): LicenseOverride | null => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return null;
};

/** The inverse, for writing the column back. */
export const licenseOverrideToColumn = (
  value: '' | LicenseOverride,
): boolean | null => (value === '' ? null : value === 'Yes');

interface CountryLine {
  country?: string;
  license_required?: boolean;
  determinable?: boolean;
}

const asCountryLines = (v: unknown): CountryLine[] =>
  Array.isArray(v) ? (v as CountryLine[]) : [];

const asString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

const fromScreening = (
  classifications: ExportClassificationResult[],
  eccn: string | null,
): LicenseRequirement => {
  // Newest country result wins; the list arrives newest-first.
  const country = classifications.find((c) => c.regime === 'country');

  if (!country) {
    return {
      value: 'Unknown',
      countries: [],
      detail: eccn
        ? 'Country Screening has not run yet.'
        : 'Set an ECCN, then run Country Screening.',
      source: 'screening',
    };
  }

  const lines = asCountryLines(country.result?.license_results);
  const needing = lines
    .filter((l) => l.license_required)
    .map((l) => asString(l.country))
    .filter((c): c is string => !!c);

  if (needing.length > 0) {
    return {
      value: 'Yes',
      countries: needing,
      detail: `Required for ${needing.join(', ')}.`,
      source: 'screening',
    };
  }

  // A status of license_required with no per-country detail still means Yes.
  if (country.status === 'license_required') {
    return {
      value: 'Yes',
      countries: [],
      detail: 'Country Screening returned License Required.',
      source: 'screening',
    };
  }

  if (country.status === 'no_data' || country.status === 'error') {
    return {
      value: 'Unknown',
      countries: [],
      detail:
        asString(country.result?.note) ||
        'Country Screening could not reach a determination.',
      source: 'screening',
    };
  }

  if (lines.length > 0) {
    // Only an explicit `license_required: false` clears a destination. A line
    // that is flagged not-determinable, or that simply omits the field, is
    // incomplete screening data — clearing the product on it would be a false
    // all-clear on an export-control question.
    const unresolved = lines.filter(
      (l) => l.determinable === false || l.license_required !== false,
    );
    if (unresolved.length > 0) {
      const countries = unresolved
        .map((l) => asString(l.country))
        .filter((c): c is string => !!c);
      return {
        value: 'Unknown',
        countries: [],
        detail: countries.length
          ? `Not determinable for ${countries.join(', ')}.`
          : 'Country Screening returned an incomplete determination.',
        source: 'screening',
      };
    }
    return {
      value: 'No',
      countries: [],
      detail: `No licence required for ${lines.length} destination${
        lines.length === 1 ? '' : 's'
      }.`,
      source: 'screening',
    };
  }

  return {
    value: 'Unknown',
    countries: [],
    detail: 'No destinations evaluated yet.',
    source: 'screening',
  };
};

/** The screening answer for a product — before any manual answer is applied. */
export const deriveLicenseRequirement = (
  classifications: ExportClassificationResult[],
  eccn: string | null,
): LicenseRequirement => fromScreening(classifications, eccn);

/**
 * Layer a reviewer's hand-entered answer over the screening one. Split out from
 * the derivation because the two are computed in different places: the parent
 * has the classification rows, the details panel has the product's override and
 * needs both values to explain the difference.
 */
export const applyLicenseOverride = (
  derived: LicenseRequirement,
  override: LicenseOverride | null,
): LicenseRequirement => {
  if (!override) return derived;
  // The manual answer is the one shown — a reviewer who has read the licence
  // outranks a lookup. The screening answer isn't discarded, though: a
  // disagreement between the two is the thing most worth surfacing, because it
  // means one of them is stale.
  const disagrees = derived.value !== 'Unknown' && derived.value !== override;
  return {
    value: override,
    countries: override === 'Yes' ? derived.countries : [],
    detail: disagrees
      ? `Set manually — Country Screening says ${derived.value}.`
      : 'Set manually.',
    source: 'manual',
  };
};
